import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  CircularProgress,
  Chip,
  Typography,
  Box,
  Button,
  TablePagination,
  TextField,
  InputAdornment,
} from "@material-ui/core";
import {
  HoverTableRow,
  TableFirstRow,
  StyledPaper,
  TableWrapper,
  PlatformWrapper,
  PlatformTitleWrapper,
  StyledAvatar,
  ProgressWrapper,
  SuccessWrapper,
  ButtonWrapperFromSsh,
  FinalWrapperFromSsh,
  ToAccessTypography,
} from "./FuiStyles";
import { XSmallHeader } from "../common/CommonStyles/CommonTypographyStyles";
import { PrimaryButton } from "../common/CommonComponents/Buttons";
import RemoveIcon from "@material-ui/icons/Remove";
import TrackChangesIcon from "@material-ui/icons/TrackChanges";
import SearchIcon from "@material-ui/icons/Search";
import axios from "axios";
import { makeStyles } from "@material-ui/core/styles";
import base from '../../config';

/**
 * Component which shows Dedicated VPC deployment requests
 * @author shilpa.adikeshava
 * @version 1.0
 */

const useStyles = makeStyles((theme) => ({
  trackButton: {
    background: 'linear-gradient(135deg, #7500C0 0%, #5a0090 100%)',
    color: 'white',
    padding: '6px 12px',
    fontSize: '0.75rem',
    minWidth: 'auto',
    '&:hover': {
      background: 'linear-gradient(135deg, #5a0090 0%, #450070 100%)',
    },
  },
}));

const DedicatedVpcRequestsList = ({ history, email }) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [dedicatedVpcData, setDedicatedVpcData] = useState([]);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch Dedicated VPC requests
  const fetchDedicatedVpcRequests = async (isBackground = false) => {
    if (!email) return;

    // Only show main loader on initial load, not on background refreshes
    if (!isBackground) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const idToken = localStorage.getItem("token");
      const response = await axios.get(
        `${base.app.apiSite}provision/mywizard-aiops/dedicated-vpc-deployment-request/fetch`,
        {
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
            "Authorization": idToken,
          },
          params: {
            email: email
          }
        }
      );

      if (response.data) {
        // Handle different possible response formats
        let data = [];

        if (Array.isArray(response.data)) {
          // Direct array response
          data = response.data;
        } else if (response.data.statusCode === 200 && response.data.body && response.data.body.data) {
          // Nested response format: {statusCode: 200, body: {data: [...]}}
          data = response.data.body.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          // Simple nested format: {data: [...]}
          data = response.data.data;
        }

        // Ensure data is an array
        if (!Array.isArray(data)) {
          data = [];
        }

        setDedicatedVpcData(data);
      }
    } catch (err) {
      console.error('Error fetching Dedicated VPC requests:', err);
      setError('Failed to fetch Dedicated VPC requests');
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  };

  // Initial fetch and refresh timer
  useEffect(() => {
    fetchDedicatedVpcRequests();
  }, [email]);

  useEffect(() => {
    if (email) {
      const timer = setInterval(() => {
        fetchDedicatedVpcRequests(true); // Pass true for background refresh
      }, 10000); // Refresh every 10 seconds like the main table
      return () => clearInterval(timer);
    }
  }, [email]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Get status chip styling
  const getStatusChipStyle = (status) => {
    const baseStyle = {
      fontSize: "11px",
      fontWeight: "600",
      height: "26px",
      minWidth: "120px"
    };

    const statusLower = status ? status.toLowerCase() : '';
    switch (statusLower) {
      case 'pending':
      case 'pending approval':
      case 'pending l1 approval':
        return {
          ...baseStyle,
          backgroundColor: "#fff4e6",
          color: "#d68910",
          border: "1px solid #f39c12"
        };
      case 'approved':
        return {
          ...baseStyle,
          backgroundColor: "#d4edda",
          color: "#155724",
          border: "1px solid #28a745"
        };
      case 'declined':
      case 'rejected':
        return {
          ...baseStyle,
          backgroundColor: "#f8d7da",
          color: "#721c24",
          border: "1px solid #dc3545"
        };
      case 'in progress':
      case 'processing':
        return {
          ...baseStyle,
          backgroundColor: "#d1ecf1",
          color: "#0c5460",
          border: "1px solid #17a2b8"
        };
      default:
        return {
          ...baseStyle,
          backgroundColor: "#e9ecef",
          color: "#495057",
          minWidth: "100px"
        };
    }
  };

  // Handle track button click (placeholder for future implementation)
  const handleTrackClick = (requestId) => {
    // TODO: Implement tracking functionality similar to OrderRequestsList
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  // Search handler
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  // Sort data by creation date (newest first)
  // Ensure dedicatedVpcData is an array before sorting
  const sortedData = Array.isArray(dedicatedVpcData)
    ? dedicatedVpcData.sort((a, b) => {
        const dateA = new Date(a.CreatedDate || a.created_at || new Date(0));
        const dateB = new Date(b.CreatedDate || b.created_at || new Date(0));
        return dateB - dateA;
      })
    : [];

  // Filter data based on search query
  const filteredData = sortedData.filter((row) => {
    if (!searchQuery.trim()) return true;

    const searchLower = searchQuery.toLowerCase();
    const requestId = (row.OrderItemUId || row.order_id || '').toString().toLowerCase();
    const cloudPlatform = (row.CloudPlatform || '').toLowerCase();
    const deploymentRegion = (row.DeploymentRegion || '').toLowerCase();
    const applications = (row.AppList || '').toLowerCase();
    const approver = (row.Approver || '').toLowerCase();
    const status = (row.ApprovalStatus || row.status || '').toLowerCase();

    return requestId.includes(searchLower) ||
           cloudPlatform.includes(searchLower) ||
           deploymentRegion.includes(searchLower) ||
           applications.includes(searchLower) ||
           approver.includes(searchLower) ||
           status.includes(searchLower);
  });

  // Get paginated data from filtered results
  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      {/* Search Bar */}
      <Box style={{ padding: "16px", backgroundColor: "#f5f5f5" }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search VPC requests by ID, platform, region, apps, approver, or status..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {isInitialLoading ? (
          <div style={{ height: "90%", maxHeight: "90%" }}>
            <ProgressWrapper
              style={{
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </ProgressWrapper>
          </div>
        ) : error && !isLoading ? (
          <FinalWrapperFromSsh>
            <SuccessWrapper>
              <StyledAvatar>
                <RemoveIcon />
              </StyledAvatar>
              <ToAccessTypography>
                {error}
              </ToAccessTypography>
            </SuccessWrapper>
            <ButtonWrapperFromSsh>
              <PrimaryButton
                onClick={() => fetchDedicatedVpcRequests()}
                label={"RETRY"}
                style={{ background: "#7500C0", color: "#FFF" }}
              />
            </ButtonWrapperFromSsh>
          </FinalWrapperFromSsh>
        ) : sortedData.length === 0 ? (
          <FinalWrapperFromSsh>
            <SuccessWrapper>
              <StyledAvatar>
                <RemoveIcon />
              </StyledAvatar>
              <ToAccessTypography>
                No Dedicated VPC requests found!
              </ToAccessTypography>
            </SuccessWrapper>
            <ButtonWrapperFromSsh>
              <PrimaryButton
                onClick={() => history.push("/fui/order/dedicatedvirtualnetworksetup")}
                label={"CREATE NEW REQUEST"}
                style={{ background: "#7500C0", color: "#FFF" }}
              />
            </ButtonWrapperFromSsh>
          </FinalWrapperFromSsh>
        ) : (
          <StyledPaper style={{ marginTop: "1%", height: "400px", display: "flex", flexDirection: "column" }}>
            <div style={{ flex: 1, overflow: "auto" }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableFirstRow align="center">REQUEST ID</TableFirstRow>
                    <TableFirstRow align="center">CLOUD PLATFORM</TableFirstRow>
                    <TableFirstRow align="center">DEPLOYMENT REGION</TableFirstRow>
                    <TableFirstRow align="center">APPLICATIONS</TableFirstRow>
                    <TableFirstRow align="center">CREATED DATE</TableFirstRow>
                    <TableFirstRow align="center">APPROVER</TableFirstRow>
                    <TableFirstRow align="center">STATUS</TableFirstRow>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((row, key) => (
                  <HoverTableRow key={key}>
                    <TableCell align="center">
                      {row.OrderItemUId || row.order_id || 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      {row.CloudPlatform || 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      {row.DeploymentRegion || 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      {row.AppList || 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      {formatDate(row.CreatedDate || row.created_at)}
                    </TableCell>
                    <TableCell align="center">
                      {row.Approver ? (
                        row.Approver.split("@")[0]
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.ApprovalStatus || row.status || 'Unknown'}
                        size="small"
                        style={getStatusChipStyle(row.ApprovalStatus || row.status)}
                      />
                    </TableCell>
                  </HoverTableRow>
                ))}
              </TableBody>
            </Table>
            </div>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              style={{ borderTop: "1px solid #e0e0e0" }}
            />
          </StyledPaper>
        )}
    </>
  );
};

DedicatedVpcRequestsList.propTypes = {
  /**
   * History property of route library
   */
  history: PropTypes.object,
  /**
   * User's email
   */
  email: PropTypes.string,
};

const MSTP = (state) => ({
  email: state.auth.userDetails.email,
});

export default connect(MSTP)(withRouter(DedicatedVpcRequestsList));